// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { formatToMoney } from '@shared/ui-kit';
import { HistValues, type KpiBoxesProps } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';
import { useAggregationData } from '@shared/advisory/aggregation/providers/aggregation-provider';
import { type LoaderStatus } from '../loader';
import { useCallback, useMemo } from 'react';
import { useAccountsTransactions, usePeriodPicker } from '../../hooks';

export const useCashflow = () => {
  const { t } = useTranslation();

  const {
    currency,
    accountOptions,
    selectedAccounts,
    institutionId,
    onChangeAccounts,
    clientId,
    updatedDate,
    refreshMode,
    onRefresh: onRefreshAggregation,
    shouldRefresh,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
  } = useAggregationData();

  const periodPicker = usePeriodPicker(HistValues['1Y']);

  const { accountsTransactionsQuery, transactions, hasTransactions } =
    useAccountsTransactions({
      clientId,
      ibans: selectedAccounts,
      start_date: periodPicker.start_date,
      institutionId,
      currency,
    });

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = [accountsTransactionsQuery.isLoading].some(Boolean);

    const isError = [accountsTransactionsQuery.isError].some(Boolean);

    const isFetching = [accountsTransactionsQuery.isFetching].some(Boolean);

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    accountsTransactionsQuery.isError,
    accountsTransactionsQuery.isFetching,
    accountsTransactionsQuery.isLoading,
  ]);

  /* istanbul ignore next */
  const { totalInflows, totalOutflows } = useMemo(() => {
    return transactions.reduce(
      (acc, transaction) => {
        if (transaction.amount && transaction.amount > 0) {
          acc.totalInflows += transaction.amount!;
        } else if (transaction.amount && transaction.amount < 0) {
          acc.totalOutflows += transaction.amount!;
        }
        return acc;
      },
      { totalInflows: 0, totalOutflows: 0 },
    );
  }, [transactions]);

  /* istanbul ignore next */
  const cashflowTableData = useMemo(() => {
    if (!transactions.length) return { data: [], months: [] };

    const monthlyData = transactions.reduce(
      (acc, transaction) => {
        const date = new Date(transaction.date);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1,
        ).padStart(2, '0')}`;

        if (!acc[monthKey]) {
          acc[monthKey] = {
            inflows: [],
            outflows: [],
            totalInflows: 0,
            totalOutflows: 0,
          };
        }

        if (transaction.amount && transaction.amount > 0) {
          acc[monthKey].inflows.push(transaction);
          acc[monthKey].totalInflows += transaction.amount!;
        } else if (transaction.amount && transaction.amount < 0) {
          acc[monthKey].outflows.push(transaction);
          acc[monthKey].totalOutflows += Math.abs(transaction.amount!);
        }

        return acc;
      },
      {} as Record<
        string,
        {
          inflows: typeof transactions;
          outflows: typeof transactions;
          totalInflows: number;
          totalOutflows: number;
        }
      >,
    );

    const sortedMonths = Object.keys(monthlyData).sort();

    const createMonthColumns = (
      months: string[],
      getValue: (month: string) => number,
    ) => {
      return months.reduce((acc, month) => {
        acc[month] = getValue(month);
        return acc;
      }, {} as Record<string, number>);
    };

    const createSubRows = (
      months: string[],
      getTransactions: (month: string) => typeof transactions,
      type: 'inflow-detail' | 'outflow-detail',
    ) => {
      return months.flatMap(month =>
        getTransactions(month).map((transaction, index) => ({
          id: `${type}-${month}-${index}`,
          concept: transaction.description || transaction.name || '',
          currency: transaction.currency || '',
          type,
          month,
          ...createMonthColumns(months, m =>
            m === month
              ? type === 'inflow-detail'
                ? transaction.amount ?? 0
                : Math.abs(transaction.amount ?? 0)
              : 0,
          ),
        })),
      );
    };

    const inflowsRow = {
      id: 'inflows',
      concept: 'Inflows',
      currency: currency,
      type: 'inflows' as const,
      ...createMonthColumns(
        sortedMonths,
        month => monthlyData[month]?.totalInflows || 0,
      ),
      parent: true,
      subRows: createSubRows(
        sortedMonths,
        month => monthlyData[month]?.inflows || [],
        'inflow-detail',
      ),
    };

    const outflowsRow = {
      id: 'outflows',
      concept: 'Outflows',
      currency: currency,
      type: 'outflows' as const,
      parent: true,
      ...createMonthColumns(
        sortedMonths,
        month => monthlyData[month]?.totalOutflows || 0,
      ),
      subRows: createSubRows(
        sortedMonths,
        month => monthlyData[month]?.outflows || [],
        'outflow-detail',
      ),
    };

    return {
      data: [inflowsRow, outflowsRow],
      months: sortedMonths,
    };
  }, [currency, transactions]);

  const kpis: KpiBoxesProps[] = useMemo(
    () => [
      {
        text: t('aggregator.cashflow.total-inflows', {
          ns: 'aggregator',
        }),
        value: formatToMoney(totalInflows, currency),
        dataTestid: 'kpi-cashflow-inflows',
        icon: 'ArrowDown',
        color: 'success',
      },
      {
        text: t('aggregator.cashflow.total-outflows', {
          ns: 'aggregator',
        }),
        value: formatToMoney(totalOutflows, currency),
        dataTestid: 'kpi-cashflow-outflows',
        icon: 'ArrowUp',
        color: 'danger',
      },
      {
        text: t('common.current-liquidity', { ns: 'common' }),
        value: formatToMoney(0, currency),
        dataTestid: 'kpi-cashflow-cash',
        icon: 'DollarSign',
        color: 'main',
      },
      {
        text: t('aggregator.cashflow.total-cashflow', { ns: 'aggregator' }),
        value: formatToMoney(totalInflows + totalOutflows, currency),
        dataTestid: 'kpi-total-cashflow',
        icon: 'ArrowLeftRight',
        color: 'secondary',
      },
    ],
    [t, totalInflows, totalOutflows, currency],
  );

  const onRefresh = useCallback(() => {
    if (shouldRefresh) {
      accountsTransactionsQuery.refetch();

      return;
    }

    onRefreshAggregation();
  }, [shouldRefresh, onRefreshAggregation, accountsTransactionsQuery]);

  return {
    t,
    status,
    kpis,
    transactions,
    hasTransactions,
    onRefresh,
    updatedDate,
    refreshMode,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    periodPicker,
    cashflowTableData,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
  };
};
